import { TaskCategory } from 'app/modules/aiAgents/aiAgentTaskCategories';
import { AiAgentTaskContext } from 'app/modules/aiAgents/models';
import type {
  EntrypointType,
  JoinType,
  ObjectType,
} from 'app/modules/aiAgentTasks/components/buildYourOwnTask/types';
import { AIResultType } from 'app/modules/aiAgentResults/models';
import { ContentBlock } from 'app/modules/aiAgentTasks/components/buildYourOwnTask/llmLoop/types';

export type TaskState =
  | 'INSTRUCTIONS_NOT_STARTED'
  | 'PREVIEW_IN_PROGRESS'
  | 'PREVIEW_COMPLETE'
  | 'PREVIEW_ERROR';

export enum AiAgentTaskAccessLevels {
  L1 = 'L1',
  L2 = 'L2',
  L3 = 'L3',
}

/** Prompt names for custom L2 task definitions. */
export enum AiAgentCustomTaskPromptName {
  CUSTOM_ONLINE_SEARCH = 'cmos',
  CUSTOM_ACCOUNT_SUMMARY = 'custom_account_summary',
  CUSTOMER_RISK_RATING = 'customer_risk_rating',
}

export enum AiTaskDataSource {
  // Entities
  FLAGGED_ENTITIES = 'FLAGGED_ENTITIES',
  ENTITIES_FROM_FLAGGED_INSTRUMENTS = 'ENTITIES_FROM_FLAGGED_INSTRUMENTS',
  COUNTERPARTY_ENTITIES = 'COUNTERPARTY_ENTITIES',
  // Instruments
  FLAGGED_INSTRUMENTS = 'FLAGGED_INSTRUMENTS',
  INSTRUMENTS_FROM_FLAGGED_ENTITIES = 'INSTRUMENTS_FROM_FLAGGED_ENTITIES',
  COUNTERPARTY_INSTRUMENTS = 'COUNTERPARTY_INSTRUMENTS',
}

export interface CustomAiAgentTaskShort {
  id: number;
  name: string;
  description: string;
  is_validated: boolean;
  requires_case_snapshot?: boolean | null;
  requires_filing_snapshot?: boolean | null;
}

export interface BaseTaskDefinition {
  id: number;
  title: string;
  description: string;
  task_category: string;
  allowed_parameters: Record<string, unknown> | null;
}

export interface AiAgentOutputResponse {
  id: number;
  content: string;
  updated_at: string;
  created_at: string;
  is_error?: boolean;
  /** Present when requested via get-ai-agent-outputs with include_raw_content. */
  step_type?: string;
  raw_content?: string | null;
}

/** Response from POST /ai-agents/get-ai-agent-outputs (get_paginated_ai_agent_outputs_endpoint). */
export interface PaginatedAiAgentOutputsResponse {
  outputs: AiAgentOutputResponse[];
  count: number;
}

export interface BaseAgentTask extends CustomAiAgentTaskShort {
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  instructions_initiated_at: string | null;
  instructions: AiAgentOutputResponse | null;
  preview_initiated_at: string | null;
  preview_relation_id: number | null;
  preview_relation_type: AiAgentTaskContext | null;
  preview: AiAgentOutputResponse | null;
  preview_log_outputs?: AiAgentOutputResponse[];
  draft_name: string | null;
  draft_description: string | null;
  ai_instructions: string | null;
  draft_ai_instructions: string | null;
  state: TaskState;
  draft_relation_id: number | null;
  draft_relation_type: AIResultType | null;
}

export interface EmptyTaskResponse extends BaseAgentTask {
  draft_task_definition: null;
  task_definition: null;
  parameters: null;
  draft_parameters: null;
}

type DraftCustomTaskResponse<
  CustomTaskDefinition extends {
    signal_agent_id: TaskCategory;
    prompt_name: string;
    access_level: AiAgentTaskAccessLevels;
  },
  Parameters extends Record<string, any>,
> = BaseAgentTask & {
  draft_parameters: Parameters;
  draft_task_definition: BaseTaskDefinition & CustomTaskDefinition;
  parameters: null;
  task_definition: null;
};

type ActiveCustomTaskResponse<
  DraftResponse extends DraftCustomTaskResponse<any, any>,
> = Omit<
  DraftResponse,
  | 'draft_task_definition'
  | 'draft_parameters'
  | 'parameters'
  | 'task_definition'
> & {
  draft_parameters: null;
  draft_task_definition: null;
  parameters: DraftResponse['draft_parameters'];
  task_definition: DraftResponse['draft_task_definition'];
};

export interface L1TaskResponse extends BaseAgentTask {
  draft_task_definition: BaseTaskDefinition & {
    signal_agent_id: TaskCategory;
    prompt_name: string;
    access_level: AiAgentTaskAccessLevels.L1;
  };
  task_definition: null;
  parameters: null;
  draft_parameters: Record<string, unknown>;
}

export interface L3TaskResponse extends BaseAgentTask {
  draft_task_definition: BaseTaskDefinition & {
    signal_agent_id: TaskCategory;
    prompt_name: string;
    access_level: AiAgentTaskAccessLevels.L3;
  };
  task_definition: BaseTaskDefinition & {
    signal_agent_id: TaskCategory;
    prompt_name: string;
    access_level: AiAgentTaskAccessLevels.L3;
  };
  parameters: Record<string, unknown>;
  draft_parameters: Record<string, unknown>;
}

export type DraftCustomOnlineResearchTaskResponse = DraftCustomTaskResponse<
  {
    signal_agent_id: TaskCategory.ONLINE_RESEARCH_SIGNAL_AGENT;
    prompt_name: AiAgentCustomTaskPromptName.CUSTOM_ONLINE_SEARCH;
    access_level: AiAgentTaskAccessLevels.L2;
  },
  {
    data_setting_ids: number[];
    search_terms: string[];
    relaxed_matching: boolean;
    data_source: AiTaskDataSource[];
    /** When grounding online research is enabled; defaults to 3 server-side if omitted. */
    max_search_queries?: number;
  }
>;

export type DraftCustomAccountSummaryTaskResponse = DraftCustomTaskResponse<
  {
    signal_agent_id: TaskCategory.ACCOUNT_RISK_SIGNAL_AGENT;
    prompt_name: AiAgentCustomTaskPromptName.CUSTOM_ACCOUNT_SUMMARY;
    access_level: AiAgentTaskAccessLevels.L2;
  },
  {
    data_setting_ids: number[];
    data_source: AiTaskDataSource[];
  }
>;

export type ActiveCustomOnlineResearchTaskResponse =
  ActiveCustomTaskResponse<DraftCustomOnlineResearchTaskResponse>;

export type OnlineResearchTaskInstructionsResponse =
  | DraftCustomOnlineResearchTaskResponse
  | ActiveCustomOnlineResearchTaskResponse;

export type ActiveCustomAccountSummaryTaskResponse =
  ActiveCustomTaskResponse<DraftCustomAccountSummaryTaskResponse>;

export type AccountSummaryTaskInstructionsResponse =
  | DraftCustomAccountSummaryTaskResponse
  | ActiveCustomAccountSummaryTaskResponse;

export type L2TaskResponse =
  | OnlineResearchTaskInstructionsResponse
  | AccountSummaryTaskInstructionsResponse
  | ActiveBYOTTaskResponse;

export interface BYOTTaskParameters {
  sql_query: string;
  sql_instructions?: string;
  join_config: {
    entrypoints: {
      id: string;
      type: EntrypointType;
      name: string;
      object_type: string;
    }[];
    objects: {
      id: string;
      name: string;
      object_type: ObjectType;
      join: {
        from: string;
        type: JoinType;
        keys: string[];
        exclude_sources: string[];
      };
      flagged_filter?: string | null;
    }[];
    requires_case_snapshot?: boolean | null;
    requires_filing_snapshot?: boolean | null;
  };
}

export type ActiveBYOTTaskResponse = BaseAgentTask & {
  task_definition: BaseTaskDefinition & {
    signal_agent_id: TaskCategory.USER_DEFINED_TASK;
    prompt_name: 'user_defined';
    access_level: AiAgentTaskAccessLevels.L2;
  };
  draft_task_definition: null;
  parameters: BYOTTaskParameters;
  draft_parameters: null;
};

export type TaskResponse =
  | EmptyTaskResponse
  | L1TaskResponse
  | L2TaskResponse
  | L3TaskResponse;

export interface LLMResponse {
  content: ContentBlock[];
  stop_reason: 'end_turn' | 'tool_use' | 'error';
  input_tokens?: number;
  output_tokens?: number;
  cost?: number;
  error?: string;
}
